import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import DataTable from "cxfcomponents/Tables/DataTable";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import SoftBox from "components/SoftBox";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import defaultConnectorIcon from "assets/images/connector-list-view.svg";
import StatusBadge from "cxfcomponents/StatusBadge";
import Checkbox from "@mui/material/Checkbox";
import { useSlugFlow } from "hooks/useSlugFlow";
import { updateFlow } from "api/FlowDesignerApis";
import { useAuth0 } from "@auth0/auth0-react";

const asConnectorArray = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value === "string") {
    try {
      return asConnectorArray(JSON.parse(value));
    } catch {
      return [value];
    }
  }
  return value && typeof value === "object" ? Object.values(value) : [];
};

const connectorId = (value) => {
  if (typeof value !== "string") return value?.id;
  try {
    const parsed = JSON.parse(value);
    return typeof parsed === "string" ? parsed : parsed?.id;
  } catch {
    return value;
  }
};

export const AddConnectorDialog = ({
  openAddConnectorDialog,
  onClose,
  connectorsWithCreator,
  connectorCreators,
}) => {
  const handleClose = () => {
    onClose();
  };
  const [selectedConnectors, setSelectedConnectors] = useState([]);
  const { flow, handleFetchFlows } = useSlugFlow();
  const { user } = useAuth0();
  const flowConnectors = useMemo(() => asConnectorArray(flow?.connectors), [flow?.connectors]);

  const handleAddConnector = async () => {
    const newConnectors = selectedConnectors
      .map((selectedConnector) => {
        const foundConnector = (connectorsWithCreator ?? []).find(
          (connector) => connector.id === selectedConnector.id
        );

        return foundConnector ? JSON.stringify(foundConnector) : null;
      })
      .filter((connector) => !!connector);
    const { id, updated_date_time, created_date_time, ...restFlow } = flow;

    const input = {
      ...restFlow,
      connectors: flowConnectors
        .map((connector) => (typeof connector === "string" ? connector : JSON.stringify(connector)))
        .concat(newConnectors),
    };

    await updateFlow(user?.stripeId, flow.id, input, true);
    handleFetchFlows();
    onClose();
  };

  const handleFilterConnector = (connector) => {
    if (flowConnectors.some((flowConnector) => connectorId(flowConnector) === connector.id)) {
      return false;
    }

    return true;
  };

  const handleConnectorAddition = (connector) => {
    setSelectedConnectors((prev) => {
      if (prev.find((prevConnector) => prevConnector.id === connector.id)) {
        return prev.filter((prevConnector) => prevConnector.id !== connector.id);
      }

      return prev.concat(connector);
    });
  };

  const tableData = useMemo(() => {
    return {
      columns: [
        {
          Header: "selected",
          accessor: "id",
          canGroupBy: false,
          disableSortBy: true,
          disableGlobalFilter: true,
          disableResizing: true,
          Cell: ({ row }) => (
            <SoftBox sx={{ maxWidth: "48px" }}>
              <Checkbox
                checked={!!selectedConnectors.find((connector) => connector.id === row.original.id)}
                onChange={() => {
                  handleConnectorAddition({
                    id: row.original.id,
                    name: row.original.name,
                    type: "Connector",
                  });
                }}
              />
            </SoftBox>
          ),
        },
        {
          Header: "developer_logo",
          accessor: "developer_logo",
          show: false,
          canGroupBy: false,
          disableSortBy: true,
        },
        {
          Header: "logo",
          accessor: "logo",
          canGroupBy: false,
          disableSortBy: true,
          Cell: ({ value }) => (
            <SoftBox width="60px" height="60px">
              {value}
            </SoftBox>
          ),
        },
        {
          Header: "name",
          accessor: "name",
          width: 250,
          canGroupBy: false,
          Cell: ({ row, value }) => (
            <SoftTypography fontSize="14px" fontWeight="bold">
              <Link
                style={{ color: "#2D3748" }}
                className="white-to-dark"
                to={`/connector/${row.original.id}`}
              >
                {value}
              </Link>
            </SoftTypography>
          ),
        },
        {
          Header: "developer",
          accessor: "developer",
          width: 250,
          canGroupBy: false,
          Cell: ({ row, value }) => (
            <SoftBox
              sx={{
                display: "flex",
                alignItems: "center",
              }}
            >
              {row.values.developer_logo}
              <SoftTypography
                marginLeft="2px"
                fontWeight="bold"
                fontSize="12px"
              >
                {value}
              </SoftTypography>
            </SoftBox>
          ),
        },
        {
          Header: "status",
          accessor: "status",
          canGroupBy: false,
          Cell: ({ value }) => {
            return <StatusBadge status={value?.toLowerCase() ?? "active"} />;
          },
        },
        {
          accessor: "description",
          show: false,
        },
      ].map((column) => ({ ...column, disableResize: true })),
      rows: (connectorsWithCreator ?? []).filter(handleFilterConnector).map((component) => {
        const creatorId = component.connector_creator_id;
        const connectorCreator = creatorId ? connectorCreators[creatorId] : null;

        return {
          id: component.id,
          name: component.name,
          status: component.status,
          logo: (
            <SoftBox
              component="img"
              width="100%"
              height="100%"
              borderRadius="12px"
              sx={{ objectFit: "contain" }}
              border={component.logo ? "0.5px solid #E6E6E6" : "none"}
              src={component.logo ? component.logo : defaultConnectorIcon}
            />
          ),
          developer: connectorCreator ? connectorCreator.company_name : "",
          developer_logo:
            connectorCreator?.logo && connectorCreator?.logo !== "null" ? (
              <SoftBox
                component="img"
                src={connectorCreator.logo}
                sx={{
                  width: "32px",
                  height: "32px",
                  objectFit: "cover",
                  borderRadius: "32px",
                }}
              />
            ) : (
              ""
            ),
          description: component.description,
        };
      }),
    };
  }, [connectorsWithCreator, connectorCreators, selectedConnectors, flowConnectors]);

  useEffect(() => {
    setSelectedConnectors([]);
  }, [openAddConnectorDialog]);

  return (
    <Dialog fullWidth maxWidth="md" open={openAddConnectorDialog} onClose={onClose}>
      <DialogContent>
        <DialogTitle>Add Connector</DialogTitle>
        <SoftBox width={{ xs: "100%" }} maxWidth={{ xl: "100px" }}></SoftBox>
        <DataTable
          table={tableData}
          entriesPerPage={{ defaultValue: 25 }}
          hiddenTopButtons
          tableView={"list"}
        />
      </DialogContent>
      <DialogActions>
        <SoftButton variant="outlined" color="info" onClick={handleClose}>
          Close
        </SoftButton>
        <SoftButton
          color="info"
          onClick={handleAddConnector}
          // disabled={toShareFlows?.length > 0 ? false : true}
        >
          Add
        </SoftButton>
      </DialogActions>
    </Dialog>
  );
};
